<template>
  <SideSheet :is-open="props.isOpen" position="right" @close="emit('close')">
    <div class="sheet" data-testid="sheet">
      <div class="sheet__close" @click="emit('close')">
        <BaseIcon icon="EF0030" width="24px" height="24px" />
      </div>
      <div class="sheet__container">
        <ChipContext
          v-if="costsDetails.isClosedMonth"
          label="Fechado"
          status="neutral"
        />
        <ChipStatus v-else label="Aberto" status="positive" />
        <Heading :level="2">
          {{ costsDetails.label }}
        </Heading>
        <BaseText
          v-if="costsDetails.disclaimer"
          class="sheet__disclaimer"
          size="sm"
        >
          {{ costsDetails?.disclaimer }}
        </BaseText>
        <div
          v-for="(item, index) in items"
          :key="index"
          :class="{ 'sheet__first-row': index === 0, sheet_row: true }"
        >
          <Row
            :left-title="item.title"
            :left-subtitle="item.subtitle"
            :has-line="item.hasLine"
          >
            <template #top-right>
              <Money>
                {{ item.value }}
              </Money>
            </template>
          </Row>
          <div v-if="item.display" class="sheet__display">
            <div
              v-for="(displayItem, displayIndex) in item.display"
              :key="displayIndex"
            >
              <div v-if="displayItem.value" class="sheet__line">
                <span class="sheet__label">{{ displayItem.label }}</span>
                <span class="sheet__amount">
                  <Money>
                    {{ displayItem.value }}
                  </Money>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SideSheet>
</template>

<script setup lang="ts">
import { IMonth } from '@/modules/cash/services/costs';
import { Money } from '@/modules/common/components/Money';
import { SideSheet } from '@/modules/common/components/SideSheet';
import {
  BaseIcon,
  BaseText,
  ChipContext,
  ChipStatus,
  Heading,
  Row
} from '@warrenbrasil/nebraska-web';
import { PropType, computed } from 'vue';

const props = defineProps({
  isOpen: Boolean,
  costsDetails: {
    type: Object as PropType<IMonth>,
    default: () => ({})
  }
});

interface DisplayItem {
  label: string;
  value: string;
}

interface Item {
  title: string;
  subtitle?: string;
  value: string;
  hasLine: boolean;
  display: DisplayItem[] | false;
}

const items = computed<Item[]>(() => [
  {
    title: 'Pendente inicial',
    subtitle: props.costsDetails.initialPendingDate,
    value: props.costsDetails.initialPendingValue,
    hasLine: false,
    display: false
  },
  {
    title: 'Custos no mês',
    value: props.costsDetails.costs,
    hasLine: true,
    display: [
      {
        label: 'Taxa sobre carteiras',
        value: props.costsDetails.wealthTaxes
      },
      {
        label: 'Taxa sobre trade',
        value: props.costsDetails.provisionedTrade
      },
      {
        label: 'Corretagem',
        value: props.costsDetails.brokerage
      },
      ...(props.costsDetails.spread
        ? [
            {
              label: 'Remuneração por intermediação',
              value: props.costsDetails.spread
            }
          ]
        : [])
    ]
  },
  {
    title: 'Pago no mês',
    value: props.costsDetails.paidValue,
    hasLine: true,
    display: [
      {
        label: 'Cashback',
        value: props.costsDetails.cashBack
      },
      {
        label: 'Resgate em carteiras',
        value: props.costsDetails.wealthReedem
      },
      {
        label: 'Carteira de cobrança',
        value: props.costsDetails.costsWealth
      },
      {
        label: 'Saldo da conta digital',
        value: props.costsDetails.accountAmount
      },
      {
        label: 'Corretagem',
        value: props.costsDetails.brokeragePaid
      },
      ...(props.costsDetails.paidOnSpread
        ? [
            {
              label: 'Remuneração por intermediação',
              value: props.costsDetails.paidOnSpread
            }
          ]
        : [])
    ]
  },
  {
    title: 'Pendente final',
    subtitle: props.costsDetails.finalPendingDate,
    value: props.costsDetails.finalPendingValue,
    hasLine: true,
    display: false
  }
]);

const emit = defineEmits(['close']);
</script>

<style lang="less" scoped>
.sheet {
  padding: @size-spacing-x450;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  height: auto;

  @media screen and (min-width: 1024px) {
    padding: @size-spacing-x1200;
  }

  &__disclaimer {
    margin-top: @size-spacing-x400;
  }

  &__container {
    width: 100%;
  }

  &__display {
    display: flex;
    flex-direction: column;
    background: @background-tertiary;
    border-radius: @size-radius-x200;
    padding: @size-spacing-x500 @size-spacing-x600;
    margin-bottom: @size-spacing-x400;
  }

  &__line {
    display: flex;
    justify-content: space-between;
    margin-top: @size-spacing-x350;
    font-size: 17px;
  }

  &__label {
    color: @element-secondary;
  }

  &__close {
    position: absolute;
    top: @size-spacing-x300;
    right: @size-spacing-x400;

    @media screen and (min-width: 1024px) {
      top: @size-spacing-x300;
      left: @size-spacing-x400;
      right: none;
    }
  }

  &__row {
    margin-top: @size-spacing-x300;
  }

  &__first-row {
    margin-top: @size-spacing-x500;
  }
}

:deep(.panel-content.right) {
  @media screen and (min-width: 1024px) {
    width: 683px;
  }
}

:deep(.container-right) {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

:deep(.row-item > div) {
  padding-top: @size-spacing-x500;
}
</style>
